import React from 'react';
import { Users, Eye, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { GlowCard } from '@/components/ui/glow-card';
type ProblemItemProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
};
const ProblemItem = ({
  icon,
  title,
  description
}: ProblemItemProps) => <div className="flex items-start gap-3 sm:gap-4 mb-6 sm:mb-8">
    <div className="rounded-full bg-luxury-100 p-1.5 sm:p-2 mt-1 flex-shrink-0">
      {icon}
    </div>
    <div>
      <h3 className="text-base sm:text-lg font-semibold text-gold mb-1 font-display">{title}</h3>
      <p className="text-sm sm:text-base text-white/90 font-sans">{description}</p>
    </div>
  </div>;
const ProblemSolutionSection = () => {
  return <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 md:px-10 bg-gradient-luxury">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Problem Section */}
          <div>
            <span className="inline-block px-3 py-1 text-xs sm:text-sm font-medium bg-luxury-800/50 text-white/90 rounded-full mb-4 sm:mb-6 font-sans">
              Problem
            </span>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-8 text-gold font-display">
              Three Ways Your Business<br className="hidden sm:block" />
              Bleeds Time & Money
            </h2>
            
            <p className="text-sm sm:text-base text-white/90 mb-6 sm:mb-10 font-sans">
              Every growing business faces these critical challenges. Sound familiar?
            </p>
            
            <div className="space-y-4 sm:space-y-6 mb-8 sm:mb-10">
              <ProblemItem 
                icon={<Clock className="h-4 w-4 sm:h-5 sm:w-5 text-highlight" />} 
                title="Slow, Expensive Development" 
                description="Traditional agencies charge $20k+ and take months. Your competitors launch MVPs in weeks while you're stuck in meetings and revisions." 
              />
              
              <ProblemItem 
                icon={<Users className="h-4 w-4 sm:h-5 sm:w-5 text-highlight" />} 
                title="WordPress That Doesn't Work Smart" 
                description="You're manually optimizing images, writing alt text, and managing content. Hours wasted on tasks AI could handle in seconds." 
              />
              
              <ProblemItem 
                icon={<Eye className="h-4 w-4 sm:h-5 sm:w-5 text-highlight" />} 
                title="AI Code Full of Security Holes" 
                description="You used ChatGPT or Claude to build fast, but now you have hardcoded secrets, broken access control, and a ticking security time bomb." 
              />
            </div>
            
            <Link to="/booking" onClick={() => window.scrollTo(0, 0)}>
              <Button size="lg" className="bg-white hover:bg-white/90 text-luxury-900 premium-button w-full sm:w-auto font-cta">
                Schedule A Call
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          
          {/* Solution Section */}
          <div className="flex flex-col items-center lg:items-start">
            <div className="lg:pt-24">
              <span className="inline-block px-3 py-1 text-xs sm:text-sm font-medium bg-highlight/20 text-highlight rounded-full mb-4 sm:mb-6 font-sans">
                Our Solution
              </span>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-gold text-center lg:text-left font-display">
                One Partner.<br className="hidden sm:block" />Three Complete Solutions.
              </h2>
              
              <p className="text-sm sm:text-base text-white/90 mb-6 sm:mb-10 text-center lg:text-left font-sans">
                Whether you need to build, secure, or automate—we've got you covered. Fixed pricing, fast delivery, and uncompromising quality.
              </p>
              
              <div className="mt-4 sm:mt-8 mb-6">
                <GlowCard glowColors={['#ff6b81', '#ff4757', '#ff6b81', '#f368e0']} glowMode="breathe" glowBlur="soft" className="p-0 overflow-hidden w-full max-w-xl mx-auto" showGlowOnHover={false}>
                  <img alt="SwipeyAI Girlfriend Chat example" className="w-full h-auto rounded-xl" src="/lovable-uploads/79d515db-0c88-4360-9c91-eb330913cba8.png" />
                </GlowCard>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default ProblemSolutionSection;